<template>
  <div class="wk_user_dashboard wrapper">
    <div class="d-flex align-center w-33 ma-3">
      <span class="wk-abet-label">user management</span>
    </div>

    <div class="mb-8" style="color: grey;">
      <p style="text-align: center;">Insert the user email and check if it is already in use.</p>
      <p style="text-align: center;">Once verified, click the button to restore the password or to create a new user.</p>
      <p style="text-align: center;">A new password will be sent to the account owner.</p>
    </div>

    <v-form
        ref="form"
        v-model="valid"
        class="wk_user_dashboard_form"
    >
      <div class="d-flex flex-row ga-3">
        <v-text-field
            class="w-50 wk_request_field"
            v-model="email"
            :rules="[(v) => !!v || 'Insert the email.']"
            label="user email*"
            placeholder="user email"
            required
            :disabled="verified"
        ></v-text-field>

        <v-select
            v-model="subsidiary"
            :items="countries"
            label="subsidiary*"
            :disabled="!verified || alreadyExists"
            item-title="title"
            item-value="value"
        >
          <template #item="{ props, item }">
            <v-list-item v-bind="props">
              <template #prepend>
                <div class="flags">
                  <span
                      v-for="flag in flags(item.value)"
                      :key="flag"
                      :class="`fi fi-${flag} mx-2`"
                  />
                </div>
              </template>
            </v-list-item>
          </template>
        </v-select>
      </div>

      <div class="wk-button-container d-flex flex-row">
        <v-btn
            variant="flat"
            color="black"
            :disabled="verified"
            @click="checkEmail"
        >Verify
        </v-btn>

        <v-btn
          variant="flat"
          color="black"
          :disabled="!verified"
          @click="reset"
        >Reset</v-btn>

        <div v-if="verified">
          <v-btn
              v-if="!alreadyExists"
              variant="tonal"
              color="black"
              @click="createUser"
          >Create user
          </v-btn>
          <v-btn
            v-else
            variant="tonal"
            color="black"
            @click="resetPassword"
          >Restore password
          </v-btn>
        </div>
      </div>

      <div class="wk_user_feedback" v-show="showMessage">{{ message }}</div>
    </v-form>
  </div>
</template>

<script setup>
// ! ATTENTION: COMPOSITION API
import {computed, onMounted, ref} from "vue";
import {API_URL, countries, notifyError} from "@/utils/const";
import authHeader from "@/services/data.service";
import axios from "axios";

import {useStore} from "vuex";
import {useRouter} from "vue-router";

const store = useStore();
const router = useRouter();

const valid = ref(false);
const email = ref('');
const subsidiary = ref('');
const verified = ref(false);
const alreadyExists = ref(false);
const created = ref(false);
const message = ref("");
const showMessage = ref(false);


const isAdmin = computed(() =>
    store.getters["auth/isAdmin"]
);

const flags = (cons) => {
  if (cons === "na") return ["us", "ca"];
  if (cons === "all") return ["it"];
  if (cons === "uk") return ["gb"];
  if (cons === "none") return [];
  return [cons];
};
onMounted(async () => {
  if (store.getters["auth/loggedIn"]) {
    await store.dispatch("auth/role");
  }

  if (!isAdmin.value) {
    await router.push({ path: "/" });
  }
});

async function checkEmail() {
  if (email.value) {
    await axios
        .get(API_URL + 'auth/username/' + email.value, { headers: authHeader() })
        .then((response) => {
          if (response.status === 200) {
            verified.value = true;
            alreadyExists.value = false;
            message.value = "The entered username is available."
          }
        })
        .catch((error) => {
          if (error.response.status === 400) {
            verified.value = true;
            alreadyExists.value = true;
            message.value = "The entered username is already in use."
          } else {
            message.value = notifyError;
          }
        });

    showMessage.value = true;
  }
}

async function createUser() {

  if (created.value) {
    message.value = "User already created. Reset the form to create a new user.";
    return;
  }

  if (email.value && !alreadyExists.value) {
    await axios
        .post(API_URL + 'auth/signup', { username: email.value, subsidiary: subsidiary.value }, { headers: authHeader() })
        .then(response => {
          if (response.status === 201) {
            created.value = true;
            message.value = "User created successfully."
          }
          if (!response.data.email) {
            message.value = "User created successfully, but there was a problem with the mail server."
          }
        })
        .catch(() => {
          created.value = false;
          message.value = notifyError;
        })
  }
}

async function resetPassword() {

  if (email.value && alreadyExists.value) {
    await axios
        .put(API_URL + 'auth/reset-password', { username: email.value }, { headers: authHeader() })
        .then(response => {
          if (response.status === 200) {
            message.value = "Password reset successfully. An email has been sent to the user."
          }
          if (!response.data.email) {
            message.value = "User reset successfully, but there was a problem with the mail server."
          }
        })
        .catch(() => {
          message.value = notifyError;
        })
  }
}

function reset() {
  email.value = '';
  verified.value = false;
  alreadyExists.value = false;
  created.value = false;
  showMessage.value = false;
  message.value = "";
}
</script>

<style lang="scss" scoped>
@use "@/styles/toolkit.abet" as *;

.wk_user_dashboard_form {
  max-width: 600px;
  margin: 0 auto;
}

.wk-button-container {
  justify-content: center;
}

.wk_user_feedback {
  margin-top: 20px;
  font-size: 0.8rem;
  text-align: center;
  color: #808080;
}

</style>